import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { OrderLineItem } from '@/types/Order';

import Note from '../../Note';
import SectionHeading from '../../SectionHeading';
import { SizeGuide } from '../../SizeGuide';
import Pagination, { type PaginationProps } from './Pagination';
import RingItem, { MultiRingItem, RingItemProps } from './RingItem';
import updateItems from './updateItems';

export interface MultiRingProps {
  readonly className?: string;
  readonly lineItems: OrderLineItem[];
  readonly isMultiPage?: boolean;
  readonly paginationProps?: PaginationProps;
  readonly products: RingItemProps['products'];
  readonly onChange: (items: OrderLineItem[]) => void;
}

const MultiRing = ({
  className,
  isMultiPage,
  lineItems,
  paginationProps = {},
  products,
  onChange,
}: MultiRingProps): JSX.Element => {
  const t = useTranslations();

  // add an "id" to each lineItem, for reference
  const itemsList = useMemo(
    () => lineItems?.map((item, id) => ({ ...item, id })) ?? [],
    [lineItems],
  );

  const handleChange = (updatedItem: MultiRingItem) => {
    const updatedItems = updateItems(itemsList, updatedItem);
    onChange(updatedItems);
  };

  return (
    <div
      data-testid="product-select-multi-ring"
      className={cx(className, 'flex flex-col gap-4')}
    >
      <div className="flex flex-col gap-2">
        <SectionHeading id="size-options-heading">
          {t('sizing_tips')}
        </SectionHeading>

        <Note>{t('eop_order_selections_page_choose_size_description')}</Note>
        <SizeGuide />
      </div>

      {isMultiPage && <Pagination {...paginationProps} />}

      <ul>
        {itemsList.map((item, idx) => (
          <li
            key={item.id}
            className={cx('py-6', 'border-b border-gray-300', {
              'border-t border-gray-300': !idx, // top border for first li
            })}
          >
            <RingItem
              item={item}
              products={products}
              onChange={(updatedItem: MultiRingItem) => {
                handleChange(updatedItem);
              }}
            />
          </li>
        ))}
      </ul>

      {isMultiPage && <Pagination {...paginationProps} />}
    </div>
  );
};

export default MultiRing;
